// Import SwipeableTextCards component
import SwipeableTextCards from "../components/SwipeableTextCards";

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { cn } from '@/lib/utils';
// Remove CardSwiper import since we're creating our own
// import { CardSwiper } from 'react-card-swiper';

// Import Shadcn UI components
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Loader2, RotateCcw, ThumbsDown, ThumbsUp, Check, X } from "@/lib/icons"
// Import Select components
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

// Add DetailedAssessmentForm import
import DetailedAssessmentForm from './DetailedAssessmentForm';
// Import the new shared component
import AssessmentSummaryReportDisplay from '../components/AssessmentSummaryReportDisplay';

// Import SimpleLayout for consistent header/footer
import SimpleLayout from '@/components/SimpleLayout';
import { Header } from '@/components/prosci/Header';

// Import TimerBar
import TimerBar from '@/components/TimerBar';

// Import ConfirmModal
import ConfirmModal from '@/components/ConfirmModal';

// Radio Group for detailed assessment
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";

// Constants
const STEPS = {
  LOADING: 'loading',
  SELECT_SECTIONS: 'select_sections',
  ENTER_IDENTIFIER: 'enter_identifier',
  FETCHING_FACTORS: 'fetching_factors',
  ASSESSMENT_CARDS: 'assessment_cards',
  DETAILED_ASSESSMENT: 'detailed_assessment',
  SUMMARY: 'summary',
  ERROR: 'error',
};

const STORAGE_KEYS = {
  CURRENT_STEP: 'assessment_current_step',
  SELECTED_CONTRIBUTORS: 'assessment_selected_contributors',
  PARTICIPANT_ID: 'assessment_participant_id',
  ACCESS_CODE: 'assessment_access_code',
  CURRENT_INDEX: 'assessment_current_index',
  FACTORS: 'assessment_factors',
  SWIPED_FACTORS: 'assessment_swiped_factors',
  ASSESSMENT_TYPE: 'assessment_type',
};

// Helper to group contributors by category (remains the same)
const groupContributorsByCategory = (contributors) => {
  return contributors.reduce((acc, contributor) => {
    const category = contributor.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(contributor);
    return acc;
  }, {});
};

// Custom card component for the swiper
function AssessmentCard({ factor, index, total }) {
  return (
    <Card className="w-full h-80 shadow-xl border border-gray-300 flex flex-col justify-between p-6 bg-card overflow-hidden ring-2 ring-offset-2 ring-primary/30">
      <div className="flex-grow overflow-auto">
        <p className="text-sm font-medium text-gray-500 mb-1">{factor.category}</p>
        <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-3">
          {factor.contributor_name}
        </p>
        <p className="text-lg font-normal text-gray-800">{factor.text}</p>
      </div>
      <p className="text-center text-xs text-gray-400 pt-2 border-t mt-2">
        Best Practice {index + 1} of {total}
      </p>
    </Card>
  );
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : '';
}

function deleteCookie(name) {
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax${secure}`;
}

// Helper function to determine score level for detailed assessments (copied from Facilitator report)
const getScoreLevel = (score) => {
  if (score >= 46) return "Extending";
  if (score >= 40) return "Proficient";
  if (score >= 32) return "Developing";
  if (score >= 22) return "Emerging";
  if (score >= 10) return "Initiating";
  return "Unknown"; // Should not happen with scores 10-50
};

// Helper function to translate detailed score to text label (copied from Facilitator report)
const getDetailedScoreLabel = (score) => {
  const numericScore = Number(score); // Ensure it's a number
  if (isNaN(numericScore) || numericScore === 0) return "Not Answered";
  switch (numericScore) {
    case 1: return "Not Applied";
    case 2: return "Minimally Applied";
    case 3: return "Partially Applied";
    case 4: return "Mostly Applied";
    case 5: return "Fully Applied";
    default: return "Not Answered"; // Or handle unexpected values
  }
};

function ParticipantAssessmentWizard(props) {
  const {
    accessCode,
    structureUrl,
    submitResponseUrl,
    csrfToken,
    assessmentType,
    // Timer props (might be undefined if not a timed assessment)
    timerEnabled = false,
    timerDurationSeconds = 30, // Default, will be overridden by prop if present
    timerDefaultAction = 'opportunity', // Default, will be overridden by prop if present
  } = props

  // --- Auth check ---
  useEffect(() => {
    const cookieCode = getCookie('assessment_code')
    if (!cookieCode || cookieCode !== accessCode) {
      window.location.href = '/'
    }
  }, [accessCode])

  // --- Logout handler ---
  const handleLogout = () => {
    deleteCookie('assessment_code')
    window.location.href = '/'
  }

  // --- State ---
  const [currentStep, setCurrentStep] = useState(() => {
    // Try to restore the current step from localStorage
    const savedStep = localStorage.getItem(STORAGE_KEYS.CURRENT_STEP)
    const savedAccessCode = localStorage.getItem(STORAGE_KEYS.ACCESS_CODE)
    // Only restore if it's the same assessment
    const initialStep = savedStep && savedAccessCode === accessCode ? savedStep : STEPS.LOADING
    return initialStep
  })

  const [contributors, setContributors] = useState([])
  const [selectedContributorIds, setSelectedContributorIds] = useState(() => {
    // Try to restore selected contributors from localStorage
    const savedContributors = localStorage.getItem(STORAGE_KEYS.SELECTED_CONTRIBUTORS)
    const savedAccessCode = localStorage.getItem(STORAGE_KEYS.ACCESS_CODE)
    return savedContributors && savedAccessCode === accessCode ? new Set(JSON.parse(savedContributors)) : new Set()
  })

  const [participantIdentifier, setParticipantIdentifier] = useState(() => {
    // Try to restore participant identifier from localStorage
    const savedId = localStorage.getItem(STORAGE_KEYS.PARTICIPANT_ID)
    const savedAccessCode = localStorage.getItem(STORAGE_KEYS.ACCESS_CODE)
    return savedId && savedAccessCode === accessCode ? savedId : ''
  })

  const [factors, setFactors] = useState(() => {
    // Try to restore factors from localStorage
    const savedFactors = localStorage.getItem(STORAGE_KEYS.FACTORS)
    const savedAccessCode = localStorage.getItem(STORAGE_KEYS.ACCESS_CODE)
    return savedFactors && savedAccessCode === accessCode ? JSON.parse(savedFactors) : []
  })

  const [currentIndex, setCurrentIndex] = useState(() => {
    // Try to restore current index from localStorage
    const savedIndex = localStorage.getItem(STORAGE_KEYS.CURRENT_INDEX)
    const savedAccessCode = localStorage.getItem(STORAGE_KEYS.ACCESS_CODE)
    return savedIndex && savedAccessCode === accessCode ? parseInt(savedIndex, 10) : 0
  })

  const [error, setError] = useState(null)
  const [lastDirection, setLastDirection] = useState()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [summaryData, setSummaryData] = useState(null)
  const [summaryError, setSummaryError] = useState(null)
  const [swipedFactors, setSwipedFactors] = useState(() => {
    const savedSwipedFactors = localStorage.getItem(STORAGE_KEYS.SWIPED_FACTORS)
    const savedAccessCode = localStorage.getItem(STORAGE_KEYS.ACCESS_CODE)
    return savedSwipedFactors && savedAccessCode === accessCode ? new Set(JSON.parse(savedSwipedFactors)) : new Set()
  })
  const [selectedViewIdentifier, setSelectedViewIdentifier] = useState('my_responses') // Added for summary view selection
  const [showInstructions, setShowInstructions] = useState(true) // Moved from renderAssessmentCards
  const [showStartOverConfirm, setShowStartOverConfirm] = useState(false) // For start over confirmation
  // const timerBarKey = useRef(0); // Keying TimerBar to currentIndex should be enough

  // Auto-hide instructions after 5 seconds
  useEffect(() => {
    if (showInstructions && currentStep === STEPS.ASSESSMENT_CARDS) {
      // Conditioned to only run for assessment cards
      const timer = setTimeout(() => {
        setShowInstructions(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [showInstructions, currentStep])

  // Reset showInstructions when moving to the ASSESSMENT_CARDS step
  useEffect(() => {
    if (currentStep === STEPS.ASSESSMENT_CARDS) {
      setShowInstructions(true)
    }
  }, [currentStep])

  // Modify handleSubmitIdentifier to store factors with contributors
  const handleSubmitIdentifier = async () => {
    if (!participantIdentifier.trim()) {
      alert('Please enter your name or an identifier.')
      return
    }

    setCurrentStep(STEPS.FETCHING_FACTORS)
    setError(null)
    setIsSubmitting(false)

    try {
      const contributorParams = Array.from(selectedContributorIds)
        .map((id) => `contributor_ids[]=${encodeURIComponent(id)}`)
        .join('&')
      const factorsUrl = `/assessment/${accessCode}/factors?${contributorParams}`

      const response = await fetch(factorsUrl)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || 'Could not fetch factors'}`)
      }
      const data = await response.json()

      const fetchedFactors = data.factors || []
      setFactors(fetchedFactors)

      // Update contributors with their factors
      const factorsByContributor = fetchedFactors.reduce((acc, factor) => {
        if (!acc[factor.contributor_id]) {
          acc[factor.contributor_id] = []
        }
        acc[factor.contributor_id].push(factor)
        return acc
      }, {})

      const updatedContributors = contributors.map((contributor) => ({
        ...contributor,
        factors: factorsByContributor[contributor.id] || [],
      }))

      setContributors(updatedContributors)

      // Save state to localStorage
      localStorage.setItem(STORAGE_KEYS.FACTORS, JSON.stringify(fetchedFactors))
      localStorage.setItem(STORAGE_KEYS.ACCESS_CODE, accessCode)
      localStorage.setItem(STORAGE_KEYS.PARTICIPANT_ID, participantIdentifier)

      if (assessmentType === 'full_detailed') {
        handleStepChange(STEPS.DETAILED_ASSESSMENT)
      } else {
        handleStepChange(STEPS.ASSESSMENT_CARDS)
      }
    } catch (error) {
      console.error('Error fetching factors:', error)
      handleStepChange(STEPS.ERROR)
      setError(error.message)
    }
  }

  // Add effect to save state changes to localStorage with proper state restoration
  useEffect(() => {
    const stateToSave = {
      currentStep,
      selectedContributorIds: Array.from(selectedContributorIds),
      participantIdentifier,
      currentIndex,
      factors,
      swipedFactors: Array.from(swipedFactors),
      contributors: contributors.map((c) => ({
        ...c,
        factors: c.factors || [], // Ensure factors are included
      })),
    }

    localStorage.setItem(STORAGE_KEYS.ACCESS_CODE, accessCode)
    localStorage.setItem(STORAGE_KEYS.CURRENT_STEP, currentStep)
    localStorage.setItem(STORAGE_KEYS.SELECTED_CONTRIBUTORS, JSON.stringify(Array.from(selectedContributorIds)))
    localStorage.setItem(STORAGE_KEYS.PARTICIPANT_ID, participantIdentifier)
    localStorage.setItem(STORAGE_KEYS.CURRENT_INDEX, currentIndex.toString())
    localStorage.setItem(STORAGE_KEYS.FACTORS, JSON.stringify(factors))
    localStorage.setItem(STORAGE_KEYS.SWIPED_FACTORS, JSON.stringify(Array.from(swipedFactors)))
    localStorage.setItem(STORAGE_KEYS.ASSESSMENT_TYPE, assessmentType)
    localStorage.setItem('contributors', JSON.stringify(contributors)) // Add contributors to storage
  }, [currentStep, selectedContributorIds, participantIdentifier, currentIndex, factors, accessCode, swipedFactors, contributors, assessmentType])

  // Modify initial useEffect to check if we're restoring state
  useEffect(() => {
    const savedAccessCode = localStorage.getItem(STORAGE_KEYS.ACCESS_CODE)
    const isRestoringState = currentStep !== STEPS.LOADING && savedAccessCode === accessCode

    if (isRestoringState) {
      // Try to restore all necessary state
      const savedContributors = localStorage.getItem('contributors')
      const savedFactors = localStorage.getItem(STORAGE_KEYS.FACTORS)

      if (savedContributors && savedFactors) {
        try {
          const parsedContributors = JSON.parse(savedContributors)
          const parsedFactors = JSON.parse(savedFactors)
          setContributors(parsedContributors)
          setFactors(parsedFactors)

          // For detailed assessment, ensure we stay on the detailed assessment step
          if (assessmentType === 'full_detailed' && currentStep === STEPS.DETAILED_ASSESSMENT) {
            console.log('Maintaining detailed assessment view on refresh')
            return // Skip fetchStructure to maintain the current state
          }
        } catch (e) {
          console.error('Failed to parse saved state:', e)
        }
      }
    }

    fetchStructure(!isRestoringState)
  }, [structureUrl, accessCode, assessmentType])

  // Add function to clear stored progress
  const clearStoredProgress = () => {
    // Clear all localStorage data first
    Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key))
    localStorage.removeItem('contributors') // Also clear contributors

    // Close the confirmation modal immediately
    setShowStartOverConfirm(false)

    // Reload the page to ensure clean state
    window.location.reload()
  }

  // Modify fetchStructure to handle assessment type
  const fetchStructure = async (shouldReset = true) => {
    if (shouldReset) {
      setError(null)
      setCurrentStep(STEPS.LOADING)
      setFactors([])
      setCurrentIndex(0)
      setLastDirection(undefined)
      setSelectedContributorIds(new Set())
      setParticipantIdentifier('')
      setIsSubmitting(false)
      setSwipedFactors(new Set()) // Add this to ensure swipedFactors is reset in fetchStructure too
    }

    try {
      const response = await fetch(structureUrl)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`HTTP error! ${response.status}, message: ${errorData.error || 'Unknown error'}`)
      }
      const data = await response.json()

      setContributors(data.contributors || [])

      // REMOVED: Automatic selection for detailed assessments removed
      // No longer automatically selecting all sections or skipping SELECT_SECTIONS for detailed mode.
      if (shouldReset || currentStep === STEPS.LOADING) {
        setCurrentStep(STEPS.SELECT_SECTIONS)
      }
    } catch (e) {
      console.error('Failed to fetch assessment structure:', e)
      setError(e.message || 'Could not load assessment details. Please check the access code and try again later.')
      setCurrentStep(STEPS.ERROR)
    }
  }

  // Fetch summary data when moving to SUMMARY step
  useEffect(() => {
    if (currentStep === STEPS.SUMMARY) {
      setSummaryError(null)
      setSummaryData(null) // Clear previous data
      const fetchSummary = async () => {
        try {
          // Append view_identifier to the URL
          const viewParam = selectedViewIdentifier === 'my_responses' ? participantIdentifier : selectedViewIdentifier
          const url = `${props.summaryUrlTemplate.replace(':access_code', props.accessCode).replace(':participant_identifier', encodeURIComponent(participantIdentifier))}?view_identifier=${encodeURIComponent(viewParam)}`

          const response = await fetch(url)
          if (!response.ok) {
            const errorBody = await response.text()
            console.error('Error fetching summary:', errorBody)
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          const data = await response.json()
          setSummaryData(data)
        } catch (error) {
          console.error('Error fetching summary:', error)
          setSummaryError(error.message || 'Failed to load summary.')
        }
      }
      fetchSummary()
    }
  }, [currentStep, props.summaryUrlTemplate, props.accessCode, participantIdentifier, selectedViewIdentifier]) // Added selectedViewIdentifier dependency

  // --- Handlers ---
  const handleContributorToggle = (contributorId) => {
    setSelectedContributorIds((prev) => {
      const newSet = new Set(prev)
      const idStr = contributorId.toString() // Ensure consistency
      if (newSet.has(idStr)) {
        newSet.delete(idStr)
      } else {
        newSet.add(idStr)
      }
      return newSet
    })
  }

  const handleSubmitSections = () => {
    if (selectedContributorIds.size === 0) {
      alert('Please select at least one section.')
      return
    }

    // Save selected contributors to localStorage
    localStorage.setItem(STORAGE_KEYS.SELECTED_CONTRIBUTORS, JSON.stringify(Array.from(selectedContributorIds)))
    localStorage.setItem(STORAGE_KEYS.ACCESS_CODE, accessCode)

    handleStepChange(STEPS.ENTER_IDENTIFIER)
  }

  // --- New: Submit Single Response Function ---
  const submitSingleResponse = async (factorId, responseValue) => {
    setIsSubmitting(true)

    try {
      const response = await fetch(submitResponseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ participantIdentifier, factorId, response: responseValue }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error(`Failed to save response for ${factorId}! status: ${response.status}, message: ${errorData.error || 'Unknown error'}`)
      } else {
        console.log(`Response for ${factorId} saved successfully.`)
      }
    } catch (e) {
      console.error(`Network error saving response for ${factorId}:`, e)
    } finally {
      setIsSubmitting(false) // Allow next swipe/submission
    }
  }

  const handleSwipe = (el, meta, id, action) => {
    // id is factor.id of the card being actioned upon
    // action is 'Strength', 'Opportunity', or 'dislike' (which maps to Opportunity)

    const responseValue = action === 'dislike' || action === 'Opportunity' ? 2 : 4
    setLastDirection(action === 'dislike' ? 'Opportunity' : action) // For UI feedback text, normalize 'dislike'

    // Update swiped factors optimistically
    const newSwipedFactorsSet = new Set(swipedFactors).add(id)
    setSwipedFactors(newSwipedFactorsSet)

    // Initiate submission
    setIsSubmitting(true)
    submitSingleResponse(id, responseValue)
      .catch((err) => {
        console.error(`Error submitting response for factor ${id}:`, err)
        // Optional: Consider reverting optimistic updates or notifying the user
        // For example, remove 'id' from swipedFactors if submission fails critically:
        // setSwipedFactors(prev => { const newSet = new Set(prev); newSet.delete(id); return newSet; });
      })
      .finally(() => {
        setIsSubmitting(false) // Reset submission state regardless of success/failure
      })

    // Advance UI to the next card or summary
    // This should happen immediately for better UX, not waiting for submission to complete.
    if (currentIndex + 1 < factors.length) {
      setCurrentIndex(currentIndex + 1)
    } else {
      // This was the last card based on currentIndex progression
      // Verify with the size of the set of factors that have been actioned upon
      if (newSwipedFactorsSet.size >= factors.length) {
        if (assessmentType === 'quick_interactive' || assessmentType === 'full_detailed') {
          // Add a small delay to ensure the final API call has time to complete
          setTimeout(() => {
            handleStepChange(STEPS.SUMMARY)
          }, 500) // 500ms delay to allow final submitResponse to complete
        }
      } else {
        // This state might occur if factors.length is dynamic or there's a mismatch.
        // For safety, if currentIndex suggests we're at the end, but swiped count doesn't match, go to summary.
        console.warn('Reached end of currentIndex in swipe, but not all factors appear swiped. Forcing summary.')
        setTimeout(() => {
          handleStepChange(STEPS.SUMMARY)
        }, 500) // Also add delay for this case
      }
    }
  }

  const handleFinish = () => {
    // Add a small delay to ensure any final API calls have time to complete
    setTimeout(() => {
      handleStepChange(STEPS.SUMMARY)
    }, 500) // 500ms delay to allow final responses to be processed
  }

  const handleStepChange = (newStep) => {
    setCurrentStep(newStep)

    // Save state when transitioning to or within detailed assessment
    if (newStep === STEPS.DETAILED_ASSESSMENT || currentStep === STEPS.DETAILED_ASSESSMENT) {
      localStorage.setItem('contributors', JSON.stringify(contributors))
      localStorage.setItem(STORAGE_KEYS.FACTORS, JSON.stringify(factors))
      localStorage.setItem(STORAGE_KEYS.ACCESS_CODE, accessCode)
      localStorage.setItem(STORAGE_KEYS.CURRENT_STEP, newStep)
      localStorage.setItem(STORAGE_KEYS.ASSESSMENT_TYPE, assessmentType)
    }
  }

  // --- Render Functions ---
  const renderLoading = () => (
    <div className="flex items-center justify-center h-[calc(100vh-8rem)] sm:h-[calc(100vh-9.3rem)] md:h-[calc(100vh-8rem)] max-sm:h-[calc(100vh-9.3rem)]">
      <Card className="w-full max-w-md p-8">
        <CardContent className="flex flex-col items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Loading Assessment...</p>
        </CardContent>
      </Card>
    </div>
  )

  const renderFetchingFactors = () => (
    <div className="flex items-center justify-center h-[calc(100vh-8rem)] sm:h-[calc(100vh-9.3rem)] md:h-[calc(100vh-8rem)] max-sm:h-[calc(100vh-9.3rem)]">
      <Card className="w-full max-w-md p-8">
        <CardContent className="flex flex-col items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Loading Questions...</p>
        </CardContent>
      </Card>
    </div>
  )

  const renderError = () => (
    <div className="flex items-center justify-center h-[calc(100vh-8rem)] sm:h-[calc(100vh-9.3rem)] md:h-[calc(100vh-8rem)] max-sm:h-[calc(100vh-9.3rem)]">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Error</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTitle>An Error Occurred</AlertTitle>
            <AlertDescription>{error || 'An unknown error occurred.'}</AlertDescription>
          </Alert>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button variant="outline" onClick={fetchStructure}>
            Start Over
          </Button>
        </CardFooter>
      </Card>
    </div>
  )

  const renderSelectSections = () => {
    const groupedContributors = groupContributorsByCategory(contributors)
    const categories = Object.keys(groupedContributors)

    return (
      <div className="flex items-center justify-center p-4 min-h-[calc(100vh-8rem)] ">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>Select Contributors</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {categories.map((category) => (
              <div key={category} className="border border-border/25 rounded-[6px] p-4 space-y-2">
                <h3 className="text-xl mb-3">{category}</h3>
                <div className="space-y-2">
                  {groupedContributors[category].map((contributor) => (
                    <div key={contributor.id} className="flex items-center space-x-2">
                      <Checkbox id={`contributor-${contributor.id}`} checked={selectedContributorIds.has(contributor.id.toString())} onCheckedChange={() => handleContributorToggle(contributor.id)} />
                      <Label htmlFor={`contributor-${contributor.id}`} className="cursor-pointer">
                        {contributor.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button onClick={handleSubmitSections} disabled={selectedContributorIds.size === 0}>
              Next
            </Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  const renderEnterIdentifier = () => (
    <div className="flex items-center justify-center p-4 h-[calc(100vh-8rem)] sm:h-[calc(100vh-9.3rem)] md:h-[calc(100vh-8rem)] max-sm:h-[calc(100vh-9.3rem)]">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Enter Your Identifier</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Label htmlFor="participantId">Your Name or Group Identifier</Label>
          <Input id="participantId" value={participantIdentifier} onChange={(e) => setParticipantIdentifier(e.target.value)} placeholder="e.g., Team Alpha, John Doe" />
          <p className="text-xs text-muted-foreground">This helps the facilitator identify your responses.</p>
        </CardContent>
        <CardFooter className="flex justify-between">
          {/* Add Back button */}
          <Button variant="outline" onClick={() => handleStepChange(STEPS.SELECT_SECTIONS)}>
            Back
          </Button>
          <Button onClick={handleSubmitIdentifier}>Next</Button>
        </CardFooter>
      </Card>
    </div>
  )

  const renderAssessmentCards = () => {
    // Original logic for empty factors / completion (simplified for brevity, ensure it matches original)
    if (!factors || factors.length === 0) {
      return <div>No factors loaded.</div> // Placeholder, match original
    }
    const remainingFactors = factors.filter((factor) => !swipedFactors.has(factor.id))
    if (remainingFactors.length === 0 && assessmentType === 'quick_interactive') {
      // Simplified, ensure original logic for summary transition is preserved
      // setTimeout(() => { if (currentStep === STEPS.ASSESSMENT_CARDS) handleStepChange(STEPS.SUMMARY); }, 100);
      // return <div>Loading summary...</div>; // Placeholder
      // The original component has more complex logic here for transitioning to summary
      // which should be preserved. For now, let's assume handleFinish covers this.
      // If currentIndex >= factors.length, it means all factors (including those already swiped)
      // have been processed by the swiper mechanism itself.
      if (currentIndex >= factors.length) {
        // This implies all cards passed through the swiper, handleFinish should have been called
        // Or if not, this is a state to trigger summary.
        if (currentStep !== STEPS.SUMMARY) {
          handleFinish() // Ensure summary is triggered if not already
        }
        return (
          <div className="flex items-center justify-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
        ) // Show loader while summary is being fetched by handleFinish
      }
    }

    const handleTimerComplete = () => {
      const currentFactor = factors[currentIndex]
      if (!currentFactor || !timerEnabled || assessmentType !== 'quick_interactive') {
        return
      }
      const action = timerDefaultAction === 'strength' ? 'Strength' : 'Opportunity'
      handleSwipe(null, null, currentFactor.id, action) // Assumes handleSwipe is defined in this component
    }

    // Original return structure of renderAssessmentCards (skeleton)
    return (
      <div className="flex flex-col items-center justify-center p-4 overflow-hidden min-h-[calc(100vh-8rem)]">
        {/* Instructions Overlay - Preserving original structure */}
        {showInstructions && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 transition-opacity" onClick={() => setShowInstructions(false)}>
            <div className="bg-card rounded-lg p-6 shadow-xl max-w-xs text-center">
              <h3 className="text-xl mb-3">How to Use</h3>
              <div className="space-y-4 mb-4">
                <p className="font-medium text-score-5 flex items-center justify-center">
                  <ThumbsUp className="mr-2 h-5 w-5" />
                  Swipe Right = Strength
                </p>
                <p className="font-medium text-score-1 flex items-center justify-center">
                  <ThumbsDown className="mr-2 h-5 w-5" />
                  Swipe Left = Opportunity
                </p>
              </div>
              <p className="text-sm text-gray-500 mb-4">Tap to dismiss this message</p>
            </div>
          </div>
        )}

        {/* Timer Bar - Renders if timer is enabled and there's a current factor AND instructions are hidden */}
        {!showInstructions && timerEnabled && assessmentType === 'quick_interactive' && factors[currentIndex] && currentIndex < factors.length && (
          <div className="w-full max-w-xs mx-auto mb-3">
            {' '}
            {/* Adjust styling as needed */}
            <TimerBar
              key={currentIndex} // Re-initializes timer when card changes
              duration={timerDurationSeconds}
              onComplete={handleTimerComplete}
            />
          </div>
        )}

        {/* Saving Indicator - Preserving original structure */}
        {isSubmitting && (
          <div className="absolute top-32 right-4 flex items-center space-x-2 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-md shadow z-20">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Saving...</span>
          </div>
        )}

        {/* Card Stack - Preserving original structure */}
        <div className="relative w-full max-w-xs">
          <SwipeableTextCards
            key={`${accessCode}-${factors.length}-${swipedFactors.size}`} // Force re-render when data changes
            items={remainingFactors} // Use remainingFactors as per original logic
            totalOriginalItems={factors.length} // Explicitly pass total original items
            swipedFactors={swipedFactors} // Pass the set of swiped factor IDs
            onSwipe={handleSwipe}
            onFinish={handleFinish}
            currentCardIndex={currentIndex} // Pass current index if SwipeableTextCards needs it
            renderCard={(factor, _indexFromSwiper, _totalFromSwiper) => <AssessmentCard factor={factor} index={currentIndex} total={factors.length} />}
            // Add any other props SwipeableTextCards expects from original implementation
          />
          {/* Feedback text - Preserving original structure */}
          <p className="mt-4 text-sm text-gray-500 z-10 text-center">Swipe or use buttons</p>
        </div>
      </div>
    )
  }

  // Add renderDetailedAssessment function
  const renderDetailedAssessment = () => {
    if (!contributors || contributors.length === 0) {
      // Try to restore assessment state from localStorage before showing error
      const savedContributors = localStorage.getItem('contributors')
      const savedFactors = localStorage.getItem(STORAGE_KEYS.FACTORS)
      const savedParticipantId = localStorage.getItem(STORAGE_KEYS.PARTICIPANT_ID)

      if (savedContributors && savedFactors && savedParticipantId && accessCode === localStorage.getItem(STORAGE_KEYS.ACCESS_CODE)) {
        try {
          const parsedContributors = JSON.parse(savedContributors)
          const parsedFactors = JSON.parse(savedFactors)

          // Only update if we actually have data
          if (parsedContributors.length > 0 && parsedFactors.length > 0) {
            setContributors(parsedContributors)
            setFactors(parsedFactors)
            setParticipantIdentifier(savedParticipantId)

            // Return loading state while we restore
            return (
              <div className="flex items-center justify-center h-[calc(100vh-8rem)] sm:h-[calc(100vh-9.3rem)] md:h-[calc(100vh-8rem)] max-sm:h-[calc(100vh-9.3rem)]">
                <Card className="w-full max-w-md p-8">
                  <CardContent className="flex flex-col items-center justify-center">
                    <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">Restoring your assessment data...</p>
                  </CardContent>
                </Card>
              </div>
            )
          }
        } catch (e) {
          console.error('Failed to restore saved assessment data:', e)
        }
      }

      // If restoration failed or no saved data found, show error with restart option
      return (
        <div className="flex items-center justify-center h-[calc(100vh-8rem)] sm:h-[calc(100vh-9.3rem)] md:h-[calc(100vh-8rem)] max-sm:h-[calc(100vh-9.3rem)]">
          <Card className="w-full max-w-md p-8">
            <CardHeader>
              <CardTitle>Assessment Data Not Found</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center">
              <p className="text-muted-foreground mb-4">Your assessment data couldn't be found or has been reset.</p>
              <Button
                variant="outline"
                onClick={() => {
                  // Don't reset everything, just restart from participant identifier
                  if (participantIdentifier) {
                    handleSubmitIdentifier() // This will refetch the factors with current participant ID
                  } else {
                    handleStepChange(STEPS.ENTER_IDENTIFIER) // Go back to enter name if no participant ID
                  }
                }}
                className="mt-4"
              >
                Restart Assessment
              </Button>
            </CardContent>
          </Card>
        </div>
      )
    }

    // Filter contributors based on selected IDs before passing to the form
    const filteredContributors = contributors.filter((contributor) => selectedContributorIds.has(contributor.id.toString()))

    return (
      <DetailedAssessmentForm
        contributors={filteredContributors}
        participantIdentifier={participantIdentifier}
        submitResponseUrl={submitResponseUrl}
        csrfToken={csrfToken}
        onComplete={() => {
          // Add a small delay to ensure any final API calls have time to complete
          setTimeout(() => {
            handleStepChange(STEPS.SUMMARY)
          }, 500) // 500ms delay to allow final responses to be processed
        }}
      />
    )
  }

  // Add renderSummary function
  const renderSummary = () => {
    if (summaryError) {
      return <div className="text-red-600 p-8">Error loading summary: {summaryError}</div>
    }
    if (!summaryData) {
      return (
        <div className="flex items-center justify-center h-[calc(100vh-8rem)] sm:h-[calc(100vh-9.3rem)] md:h-[calc(100vh-8rem)] max-sm:h-[calc(100vh-9.3rem)]">
          <Card className="w-full max-w-md p-8">
            <CardContent className="flex flex-col items-center justify-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Loading summary...</p>
            </CardContent>
          </Card>
        </div>
      )
    }

    const {
      structure = [],
      factor_stats = [],
      contributor_summaries = [],
      current_view_identifier, // Destructure for use
    } = summaryData

    // Calculate statistics similar to FacilitatorAssessmentReport
    const answeredFactors = factor_stats.filter((f) => f.response_count > 0);

    let strengthFactorsCount = 0;
    let opportunityFactorsCount = 0;
    let mixedFactorsCount = 0;

    // Classify each factor based on its average score (like FacilitatorAssessmentReport)
    answeredFactors.forEach(factor => {
      const avgScore = factor.avg_score;
      if (avgScore >= 4) { // Threshold for Strength
        strengthFactorsCount++;
      } else if (avgScore <= 2) { // Threshold for Opportunity
        opportunityFactorsCount++;
      } else { // Mixed (avgScore > 2 && avgScore < 4)
        mixedFactorsCount++;
      }
    });

    const statistics = {
      totalResponses: factor_stats.reduce((total, f) => total + (f.response_count || 0), 0),
      uniqueFactorsAssessed: answeredFactors.length,
      strengthCount: strengthFactorsCount,
      opportunityCount: opportunityFactorsCount,
      mixedResponsesCount: mixedFactorsCount,
    }

    // Add percentage calculations based on unique factors assessed
    statistics.strengthPercentage = statistics.uniqueFactorsAssessed ? ((statistics.strengthCount / statistics.uniqueFactorsAssessed) * 100).toFixed(1) : '0'
    statistics.opportunityPercentage = statistics.uniqueFactorsAssessed ? ((statistics.opportunityCount / statistics.uniqueFactorsAssessed) * 100).toFixed(1) : '0'
    statistics.mixedPercentage = statistics.uniqueFactorsAssessed ? ((statistics.mixedResponsesCount / statistics.uniqueFactorsAssessed) * 100).toFixed(1) : '0'

    // --- Define helper functions for the AssessmentSummaryReportDisplay props ---

    // Define getParticipantBadgeStyle based on summaryData
    const getParticipantBadgeStyleForProp = (factor, assessmentType) => {
      const baseClasses = 'px-2 py-0.5 rounded-full text-xs font-semibold'
      let bgColorClass = 'bg-gray-200' // Default for Not Answered / Neutral
      let textColorClass = 'text-gray-700'

      if (assessmentType === 'full_detailed') {
        const score = Number(factor?.participant_score) // Use participant's score from factor stats
        if (isNaN(score) || score === 0) {
          // Keep default gray
        } else if (score < 1.5) {
          bgColorClass = 'bg-score-1'
          textColorClass = 'text-score-1-foreground'
        } else if (score < 2.5) {
          bgColorClass = 'bg-score-2'
          textColorClass = 'text-score-2-foreground'
        } else if (score < 3.5) {
          bgColorClass = 'bg-score-3'
          textColorClass = 'text-score-3-foreground'
        } else if (score < 4.5) {
          bgColorClass = 'bg-score-4'
          textColorClass = 'text-score-4-foreground'
        } else {
          bgColorClass = 'bg-score-5'
          textColorClass = 'text-score-5-foreground'
        }
      } else {
        // Simple Assessment (includes quick_interactive)
        // For "All Groups" view, use average score with correct thresholds
        if (current_view_identifier === 'all' || !current_view_identifier) {
          const avgScore = factor?.avg_score
          if (avgScore == null || avgScore === 0) {
            // Keep default gray for not answered
          } else if (avgScore >= 4) {
            bgColorClass = 'bg-score-5'
            textColorClass = 'text-score-5-foreground'
          } else if (avgScore <= 2) {
            bgColorClass = 'bg-score-3'
            textColorClass = 'text-score-3-foreground'
          } else {
            // Mixed: between 2 and 4 - always use gray
            bgColorClass = 'bg-gray-400'
            textColorClass = 'text-gray-100'
          }
        } else {
          // Individual participant view
          if (!factor?.participant_answered) {
            return `${baseClasses} ${bgColorClass} ${textColorClass}` // Keep default gray if not answered
          }

          const isMixed = factor?.participant_strength && factor?.participant_opportunity
          const isStrength = factor?.participant_strength && !isMixed
          const isOpportunity = factor?.participant_opportunity && !isMixed

          if (assessmentType === 'quick_interactive') {
            if (isStrength) {
              bgColorClass = 'bg-score-5'
              textColorClass = 'text-score-5-foreground'
            } else if (isOpportunity) {
              bgColorClass = 'bg-score-3'
              textColorClass = 'text-score-3-foreground'
            } else {
              // Mixed or Neutral for quick_interactive
              bgColorClass = 'bg-gray-400'
              textColorClass = 'text-gray-100'
            }
          } else {
            // Other simple assessment types
            if (isStrength) {
              bgColorClass = 'bg-score-4'
              textColorClass = 'text-score-4-foreground'
            } else if (isOpportunity) {
              bgColorClass = 'bg-score-2'
              textColorClass = 'text-score-2-foreground'
            } else {
              // Mixed or Neutral for other simple types
              bgColorClass = 'bg-gray-200'
              textColorClass = 'text-gray-700'
            }
          }
        }
      }
      return `${baseClasses} ${bgColorClass} ${textColorClass}`
    }

    // Define getParticipantFactorLabel based on summaryData
    const getParticipantFactorLabelForProp = (factor, assessmentType) => {
      if (assessmentType === 'full_detailed') {
        return getDetailedScoreLabel(factor?.participant_score)
      } else {
        // For "All Groups" view, use average score with correct thresholds
        if (current_view_identifier === 'all' || !current_view_identifier) {
          const avgScore = factor?.avg_score
          if (avgScore == null || avgScore === 0) return 'Not Answered'

          if (avgScore >= 4) {
            return 'Strength'
          } else if (avgScore <= 2) {
            return 'Opportunity'
          } else {
            // Mixed: between 2 and 4
            if (assessmentType === 'quick_interactive') {
              if (avgScore < 3) {
                return 'Mixed / O'
              } else if (avgScore > 3) {
                return 'Mixed / S'
              }
            }
            return 'Mixed'
          }
        } else {
          // Individual participant view
          if (!factor?.participant_answered) return 'Not Answered'
          if (factor?.participant_strength && factor?.participant_opportunity) return 'Mixed'
          if (factor?.participant_strength) return 'Strength'
          if (factor?.participant_opportunity) return 'Opportunity'
          return 'Neutral' // If answered but not S/O
        }
      }
    }

    // Define getParticipantSectionSubtitle based on summaryData
    const getParticipantSectionSubtitleForProp = (summary, assessmentType) => {
      const baseBadgeClasses = 'px-2 py-0.5 rounded-full text-xs font-semibold font-sans mr-1'
      const isComplete = summary.total_factors > 0 && summary.answered_factors === summary.total_factors

      if (isComplete) {
        if (assessmentType === 'full_detailed') {
          const score = summary.participant_total_score // Use participant specific score
          const level = getScoreLevel(score)
          const text = `Score: ${score} (${level})`
          let scoreBgColor = 'bg-gray-200'
          let scoreTextColor = 'text-gray-700'
          if (score === undefined || score === null) {
            /* Keep default */
          } else if (score >= 46) {
            scoreBgColor = 'bg-score-5'
            scoreTextColor = 'text-score-5-foreground'
          } else if (score >= 40) {
            scoreBgColor = 'bg-score-4'
            scoreTextColor = 'text-score-4-foreground'
          } else if (score >= 32) {
            scoreBgColor = 'bg-score-3'
            scoreTextColor = 'text-score-3-foreground'
          } else if (score >= 22) {
            scoreBgColor = 'bg-score-2'
            scoreTextColor = 'text-score-2-foreground'
          } else if (score >= 10) {
            scoreBgColor = 'bg-score-1'
            scoreTextColor = 'text-score-1-foreground'
          }
          return <span className={cn(baseBadgeClasses, scoreBgColor, scoreTextColor)}>{text}</span>
        } else {
          // Simple assessment
          const S = summary.participant_strength_count
          const O = summary.participant_opportunity_count
          const M = summary.participant_mixed_count
          // Check current_view_identifier from summaryData to hide Mixed for single participant
          const isSingleView = summaryData?.current_view_identifier && summaryData.current_view_identifier !== 'all'

          if (typeof S === 'number' && typeof O === 'number' && (typeof M === 'number' || isSingleView)) {
            return (
              <>
                <span className={cn(baseBadgeClasses, 'bg-score-4', 'text-score-4-foreground')}>S: {S}</span>
                <span className={cn(baseBadgeClasses, 'bg-score-2', 'text-score-2-foreground')}>O: {O}</span>
                {!isSingleView &&
                  typeof M === 'number' && ( // Conditionally render Mixed count
                    <span className={cn(baseBadgeClasses, 'bg-gray-200', 'text-gray-700')}>M: {M}</span>
                  )}
              </>
            )
          } else {
            console.warn(`Participant S/O/M counts missing/invalid for contributor ${summary.key}`)
            return <span className={cn(baseBadgeClasses, 'bg-gray-200', 'text-gray-700')}>Complete</span>
          }
        }
      } else if (summary.answered_factors > 0) {
        return <span className={cn(baseBadgeClasses, 'bg-gray-200', 'text-gray-700')}>Incomplete</span>
      } else if (summary.total_factors === 0) {
        return <span className={cn(baseBadgeClasses, 'bg-gray-100', 'text-gray-500')}>No Best Practices</span>
      } else {
        return <span className={cn(baseBadgeClasses, 'bg-gray-200', 'text-gray-700')}>Not Started</span>
      }
    }

    // Patch: Fix section score calculation for full_detailed
    const patchedContributorSummaries = (contributor_summaries || []).map((summary) => {
      if (props.assessmentType === 'full_detailed') {
        // For this section, get all factors
        const factors = (structure.find((c) => c.id === summary.key) || {}).factors || []
        // For each factor, get its avg_score from factor_stats
        const factorAverages = factors.map((factor) => {
          const stat = (factor_stats || []).find((f) => f.key === factor.id)
          return stat && typeof stat.avg_score === 'number' ? stat.avg_score : 0
        })
        // Section score is the sum of averages, rounded
        const sectionScore = Math.round(factorAverages.reduce((sum, avg) => sum + avg, 0))
        return { ...summary, participant_total_score: sectionScore }
      }
      return summary
    })

    return (
      <div>
        <Header title="Assessment Results" size="small">
          <Button variant="destructive-outline" onClick={() => setShowStartOverConfirm(true)}>
            <RotateCcw className="h-4 w-4" />
            Start Over
          </Button>
        </Header>
        <div className="container mx-auto p-4 md:p-6 space-y-6">
          {/* View Selector Dropdown */}
          <div className="my-4">
            <Label htmlFor="viewSelector" className="mr-2">
              Show data for:
            </Label>
            <Select value={selectedViewIdentifier} onValueChange={setSelectedViewIdentifier}>
              <SelectTrigger id="viewSelector" className="w-full sm:w-[250px]">
                <SelectValue placeholder="Select view" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="my_responses">My Responses</SelectItem>
                <SelectItem value="all">All Groups...</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Full-width Assessment Overview Card */}
          <Card>
            <CardHeader>
              <CardTitle>Assessment Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <h3 className="text-xl mb-2">Response Statistics</h3>
                  <div className="space-y-2">
                    <p>Total Responses: {statistics.totalResponses}</p>
                    <p>Unique Practices Assessed: {statistics.uniqueFactorsAssessed}</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl mb-2">Overall Results</h3>
                  <div className="space-y-2">
                    <p>Strengths: {statistics.strengthCount} ({statistics.strengthPercentage}%)</p>
                    <p>Opportunities: {statistics.opportunityCount} ({statistics.opportunityPercentage}%)</p>
                    {props.assessmentType === 'quick_interactive' && (
                      <p>Mixed Responses: {statistics.mixedResponsesCount} ({statistics.mixedPercentage}%)</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Two-column layout for grid and detailed scores */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Left column - Visual Grid */}
            <Card>
              <CardHeader>
                <CardTitle>Visual Summary Grid</CardTitle>
              </CardHeader>
              <CardContent>
                <AssessmentSummaryReportDisplay
                  structure={structure || []}
                  factorStats={factor_stats || []} // Use factor_stats from summaryData
                  contributorSummaries={patchedContributorSummaries}
                  assessmentType={props.assessmentType} // Pass assessmentType from props
                  getFactorBadgeStyle={getParticipantBadgeStyleForProp} // Pass the function
                  getFactorLabel={getParticipantFactorLabelForProp} // Pass the function
                  getSectionSubtitleElement={getParticipantSectionSubtitleForProp} // Pass the function
                  currentViewIdentifier={current_view_identifier || 'all'} // Pass current_view_identifier
                  displayMode="grid" // Only show the grid
                />
              </CardContent>
            </Card>

            {/* Right column - Detailed Contributor Scores */}
            <Card>
              <CardHeader>
                <CardTitle>Detailed Contributor Scores</CardTitle>
              </CardHeader>
              <CardContent>
                <AssessmentSummaryReportDisplay
                  structure={structure || []}
                  factorStats={factor_stats || []} // Use factor_stats from summaryData
                  contributorSummaries={patchedContributorSummaries}
                  assessmentType={props.assessmentType} // Pass assessmentType from props
                  getFactorBadgeStyle={getParticipantBadgeStyleForProp} // Pass the function
                  getFactorLabel={getParticipantFactorLabelForProp} // Pass the function
                  getSectionSubtitleElement={getParticipantSectionSubtitleForProp} // Pass the function
                  currentViewIdentifier={current_view_identifier || 'all'} // Pass current_view_identifier
                  displayMode="accordion" // Only show the accordion
                />
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">Thank you for completing the assessment! Your responses have been recorded.</p>
            <Button onClick={handleLogout} variant="default">
              Exit Assessment
            </Button>
          </div>
        </div>
        
        {/* Start Over Confirmation Modal */}
        <ConfirmModal open={showStartOverConfirm} title="Start Over" message="Are you sure you want to start over? This will clear all your responses and progress. This action cannot be undone." onCancel={() => setShowStartOverConfirm(false)} onConfirm={clearStoredProgress} confirmLabel="Start Over" />
      </div>
    )
  }

  // --- Render Functions ---
  const renderStep = () => {
    switch (currentStep) {
      case STEPS.LOADING:
        return renderLoading()
      case STEPS.SELECT_SECTIONS:
        return renderSelectSections()
      case STEPS.ENTER_IDENTIFIER:
        return renderEnterIdentifier()
      case STEPS.FETCHING_FACTORS:
        return renderFetchingFactors()
      case STEPS.ASSESSMENT_CARDS:
        return renderAssessmentCards()
      case STEPS.DETAILED_ASSESSMENT:
        return renderDetailedAssessment()
      case STEPS.SUMMARY:
        return renderSummary()
      case STEPS.ERROR:
        return renderError()
      default:
        return null
    }
  }

  return <SimpleLayout>{renderStep()}</SimpleLayout>
}

ParticipantAssessmentWizard.propTypes = {
  accessCode: PropTypes.string.isRequired,
  structureUrl: PropTypes.string.isRequired,
  submitResponseUrl: PropTypes.string.isRequired,
  summaryUrlTemplate: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  assessmentType: PropTypes.string.isRequired,
  // Timer props (might be undefined if not a timed assessment)
  timerEnabled: PropTypes.bool,
  timerDurationSeconds: PropTypes.number,
  timerDefaultAction: PropTypes.string,
};

export default ParticipantAssessmentWizard;
